import { type ConfigType, type Dayjs, date } from 'configuration/dayjs'
import {
    DAY_FORMAT,
    HOUR_FORMAT,
    MONTH_FORMAT,
    UNDEFINED_VALUE,
    YEAR_FORMAT,
} from 'const/formatting'
import { formatJSONDateTime } from 'helpers/formatting'
import type { Aggregation } from 'types'

export const TIME_SERIES_TOOLTIP_DATE_RANGE_SEPARATOR = '\u2013'

export type DateAggregation =
    | 'hour'
    | 'day'
    | 'week'
    | 'month'
    | 'year'
    | 'quarter'

const formatByAggregation: Record<DateAggregation, string> = {
    hour: HOUR_FORMAT,
    day: DAY_FORMAT,
    week: DAY_FORMAT,
    month: MONTH_FORMAT,
    quarter: YEAR_FORMAT,
    year: YEAR_FORMAT,
}

function formatCalendarRangeAbbreviated(
    start: Dayjs,
    end: Dayjs,
    rangeSeparator: string
): string {
    const sameYear = start.year() === end.year()
    const sameMonth = sameYear && start.month() === end.month()

    if (sameMonth) {
        return `${start.format('MMM D')} ${rangeSeparator} ${end.format('D')}, ${end.format('YYYY')}`
    }
    if (sameYear) {
        return `${start.format('MMM D')} ${rangeSeparator} ${end.format('MMM D')}, ${end.format('YYYY')}`
    }
    return `${start.format('MMM D, YYYY')} ${rangeSeparator} ${end.format('MMM D, YYYY')}`
}

const ISO_CALENDAR_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/

// dayjs's loose parser rolls overflowed dates forward (2026-02-30 → Mar 2),
// so the calendar part is validated strictly.
function parseAnchorDay(anchorDay: string): Dayjs {
    const calendarDate = anchorDay.match(ISO_CALENDAR_DATE_PREFIX)?.[0]
    if (!calendarDate) {
        return date(anchorDay)
    }
    const strict = date(calendarDate, 'YYYY-MM-DD', true)
    if (!strict.isValid() || calendarDate === anchorDay) {
        return strict
    }
    return date(anchorDay)
}

export const formatTimeSeriesCardTooltipBucketLabel = (
    anchorDay: string | undefined | null,
    aggregation: Aggregation | undefined,
    rangeSeparator: string
): string => {
    if (!anchorDay) {
        return ''
    }

    const parsed = parseAnchorDay(anchorDay)

    if (!parsed.isValid()) {
        return anchorDay
    }

    if (aggregation === 'year') {
        return parsed.format('YYYY')
    }

    if (aggregation === 'week') {
        const weekStart = parsed.startOf('week')
        const weekEnd = parsed.endOf('week')
        return formatCalendarRangeAbbreviated(
            weekStart,
            weekEnd,
            rangeSeparator
        )
    }

    if (aggregation === 'month') {
        const monthStart = parsed.startOf('month')
        const monthEnd = parsed.endOf('month')
        return formatCalendarRangeAbbreviated(
            monthStart,
            monthEnd,
            rangeSeparator
        )
    }

    return parsed.format('MMM D, YYYY')
}

export const formatDate = (
    value: ConfigType,
    aggregation: DateAggregation | null = null,
    customFormat: string = DAY_FORMAT,
    formatAsWeek: boolean = false
): string => {
    if (!value) {
        return UNDEFINED_VALUE
    }

    const format = aggregation ? formatByAggregation[aggregation] : customFormat

    if (aggregation === 'week' || formatAsWeek) {
        const weekStart = date(value)
        const weekEnd = weekStart.add(6, 'days')
        return `${weekStart.format(format)} – ${weekEnd.format(format)}`
    }

    if (aggregation === 'quarter') {
        const quarter = Math.floor(date(value).month() / 3) + 1
        return `Q${quarter} ${date(value).format(format)}`
    }

    return date(value).format(format)
}

export const PACIFIC_TIMEZONE = 'America/Los_Angeles'
export const UTC = 'UTC'

export function isoInstantToPacificStartOfDay(
    iso: string | null | undefined
): Dayjs | null {
    if (!iso) {
        return null
    }
    return date(iso).tz(PACIFIC_TIMEZONE).startOf('day')
}

export function isoInstantToPacificDayKey(
    iso: string | null | undefined
): string | null {
    if (!iso) {
        return null
    }
    return date(iso).tz(PACIFIC_TIMEZONE).format('YYYY-MM-DD')
}

export function pacificCalendarDayStringToEndOfDayUtcIso(
    dayStr: string
): string {
    return date
        .tz(dayStr, 'YYYY-MM-DD', PACIFIC_TIMEZONE)
        .endOf('day')
        .utc()
        .toISOString()
}

export const moveDateBetweenTimeZones = (
    value: string,
    originalTZ: string,
    targetTZ: string
): string => formatJSONDateTime(date.tz(value, originalTZ).tz(targetTZ))

// Formats a tuple of date strings into a human-readable date range
// e.g. ['2023-01-01', '2023-01-31'] => 'Jan 1 - Jan 31'
// e.g. ['2023-12-15', '2024-01-15'] => 'Dec 15 2023 - Jan 15 2024'
export const formatDateRangeTuple = (
    dates: string[],
    locale: string = 'en-US'
): string => {
    if (!Array.isArray(dates) || dates.length !== 2) {
        return ''
    }
    const [startStr, endStr] = dates
    const start = new Date(startStr)
    const end = new Date(endStr)
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return ''
    }

    const sameYear = start.getFullYear() === end.getFullYear()
    const fmt = (d: Date): string =>
        d.toLocaleString(locale, { month: 'short', day: 'numeric' })

    return sameYear
        ? `${fmt(start)} - ${fmt(end)}`
        : `${fmt(start)} ${start.getFullYear()} - ${fmt(end)} ${end.getFullYear()}`
}

/**
 * Relative date formatting - currently only used in the Segment filter. Examples:
 * - Within last hour: "X minutes ago"
 * - Today (but not within last hour): "X hours ago"
 * - Yesterday: "yesterday"
 * - This year: "Nov 18" format
 * - Older: "Oct 14, 2024" format
 */
export const formatRelativeDate = (value: ConfigType): string => {
    if (!value) {
        return UNDEFINED_VALUE
    }

    const then = date(value)
    const now = date()

    const minutesAgo = now.diff(then, 'minutes')
    const hoursAgo = now.diff(then, 'hours')

    // Within the last hour
    if (minutesAgo < 60) {
        if (minutesAgo < 1) {
            return 'just now'
        }
        return `${minutesAgo} ${minutesAgo === 1 ? 'minute' : 'minutes'} ago`
    }

    // Today (but not within last hour)
    if (then.isSame(now, 'day')) {
        return `${hoursAgo} ${hoursAgo === 1 ? 'hour' : 'hours'} ago`
    }

    // Yesterday
    if (then.isSame(now.subtract(1, 'day'), 'day')) {
        return 'yesterday'
    }

    // This year
    if (then.isSame(now, 'year')) {
        return then.format('MMM D')
    }

    // Older (different year)
    return then.format('MMM D, YYYY')
}
